import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
    Avatar,
    Box,
    CssBaseline,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Breadcrumbs,
    Link,
    Chip,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import AxiosInstance from '../../services/networkServices';
import Spinner from "../../components/Spinner";
import { showToast } from '../../store/slices/common';
import AccountActionDialog, { getAuthorAccountState } from './AccountActionDialog';

const CommentDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [commentStatus, setCommentStatus] = useState('Active');
    const [commentInfo, setCommentInfo] = useState(null);
    const [commentAuthor, setCommentAuthor] = useState(null);
    const [reportedUsers, setReportedUsers] = useState([]);
    const [accountDialogOpen, setAccountDialogOpen] = useState(false);
    const [accountActionBusy, setAccountActionBusy] = useState(false);
    const { commentId } = useParams();
    useEffect(() => {
        const fetchReportedUsers = async () => {
            setLoading(true);
            try {
                const response = await AxiosInstance.post('/report/list-reported-users', {
                    page: 1,
                    limit: 10,
                    id: commentId, // Ensure this is the correct parameter
                    // type: 'COMMENT'
                });
                if (response?.data?.data?.list) {
                    const list = response.data.data.list;
                    // Reporter accounts can be deleted after reporting — the
                    // report row must still render, so never assume
                    // userDetails exists.
                    const formattedUsers = list.map(user => ({
                        id: user?._id,
                        username: user?.userDetails?.username || 'Deleted user', // Reporter Username
                        email: user?.userDetails?.email || '—', // Reporter Email
                        reason: user?.issue, // Reason
                        description: user?.issueDescription, // Description
                        date: user?.createdAt // Date of Report
                    }));
                    setReportedUsers(formattedUsers);
                    // Only the first row carries the reported comment itself
                    // and the account that wrote it.
                    const comment = list?.[0]?.commentDetails || null;
                    setCommentInfo(comment);
                    setCommentAuthor(list?.[0]?.commentBy || null);
                    if (comment?.isDisabled) {
                        setCommentStatus('Disabled');
                    }
                } else {
                    console.warn('No reported users found or incorrect data format:', response.data);
                    setReportedUsers([]);
                }
            } catch (error) {
                console.error('Failed to fetch reported users:', error);
                setReportedUsers([]);
            } finally {
                setLoading(false);
            }
        };

        if (commentId) { // Ensure commentId is available before making the request
            fetchReportedUsers();
        }
    }, [commentId]);

    const getCommentStatusChip = () => {
        if (commentInfo?.isDeleted) {
            return <Chip label="Comment Deleted" size="small" sx={{ backgroundColor: '#9e9e9e', color: '#fff', fontWeight: 600 }} />;
        }
        if (commentStatus === 'Disabled') {
            return <Chip label="Comment Disabled" size="small" color="error" sx={{ fontWeight: 600 }} />;
        }
        return <Chip label="Comment Enabled" size="small" color="success" sx={{ fontWeight: 600 }} />;
    };




    const handleDisableComment = async () => {
        try {

            const payload = {
                action: 'DISABLE',
                type: 'COMMENT',
                id: commentId
            };

            const response = await AxiosInstance.post('/report/action', payload);

            if (response.data.status) {
                setCommentStatus('Disabled');
            } else {
                console.error('Failed to disable comment');
            }
        } catch (error) {
            console.error('Failed to disable comment', error);
        }
    };

    const accountState = getAuthorAccountState(commentAuthor);
    const canDisableAccount = accountState === 'active';

    const handleDisableAccount = async () => {
        setAccountActionBusy(true);
        try {
            await AxiosInstance.post('/report/action', {
                action: 'DISABLE_ACCOUNT',
                type: 'COMMENT',
                id: commentId
            });
            setCommentAuthor((prev) =>
                prev ? { ...prev, isAccountBlocked: true } : prev
            );
            dispatch(
                showToast({
                    color: 'success',
                    msg: `@${commentAuthor?.username || 'User'}'s account has been disabled`
                })
            );
            setAccountDialogOpen(false);
        } catch (error) {
            dispatch(
                showToast({
                    color: 'error',
                    msg:
                        error?.response?.data?.message ||
                        "Failed to disable the comment author's account"
                })
            );
        } finally {
            setAccountActionBusy(false);
        }
    };

    const getAccountStatusChip = () => {
        if (accountState === 'banned') {
            return <Chip label="Account Banned" size="small" color="error" sx={{ fontWeight: 600 }} />;
        }
        if (accountState === 'deleted') {
            return <Chip label="Account Deleted" size="small" sx={{ backgroundColor: '#9e9e9e', color: '#fff', fontWeight: 600 }} />;
        }
        if (accountState === 'disabled') {
            return <Chip label="Account Disabled" size="small" color="error" sx={{ fontWeight: 600 }} />;
        }
        return <Chip label="Account Active" size="small" color="success" sx={{ fontWeight: 600 }} />;
    };

    const handleBreadcrumbClick = (event) => {
        event.preventDefault();
        navigate('/manage-ticket', { state: { filterType: 'COMMENT' } });
    };
    const formatDateString = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    return (<>
        <CssBaseline />
        <Box>
            {loading ? (<Spinner />) : (<Box>
                <Box sx={{ marginBottom: '24px' }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px', flexWrap: 'wrap', gap: '10px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Typography variant="h5" sx={{
                                fontSize: {
                                    xs: '15px', sm: '16px', md: '18px', lg: '20px', xl: '24px'
                                },
                            }} component="h1">
                                Comment Details
                            </Typography>
                            {getCommentStatusChip()}
                            {getAccountStatusChip()}
                        </Box>
                        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{
                                    borderRadius: '6px', fontSize: '14px', backgroundColor: 'red'
                                }}
                                onClick={handleDisableComment}
                                disabled={commentStatus === 'Disabled'}
                            >
                                {commentStatus === 'Disabled' ? 'Comment Disabled' : 'Disable Comment'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                sx={{
                                    borderRadius: '6px', fontSize: '14px'
                                }}
                                onClick={() => setAccountDialogOpen(true)}
                                disabled={!canDisableAccount || accountActionBusy}
                            >
                                {canDisableAccount
                                    ? 'Disable Account'
                                    : accountState === 'deleted'
                                    ? 'Account Deleted'
                                    : 'Account Disabled'}
                            </Button>
                        </Box>
                    </Box>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                        <Link underline="hover" color="inherit" href="#" onClick={handleBreadcrumbClick}>
                            Manage Tickets
                        </Link>
                        <Typography color="textPrimary">Comment Details</Typography>
                    </Breadcrumbs>
                </Box>
                {commentInfo?.content && (
                    <Box sx={{
                        padding: '12px 15px',
                        marginBottom: '15px',
                        borderRadius: '6px',
                        backgroundColor: '#f7f7f7',
                        border: '1px solid #e0e0e0'
                    }}>
                        <Typography sx={{ fontWeight: 600, marginBottom: '6px', fontSize: '14px', color: '#142C5C' }}>
                            Reported Comment
                        </Typography>
                        <Typography sx={{ color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {commentInfo.content}
                        </Typography>
                    </Box>
                )}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    padding: '12px 15px',
                    marginBottom: '15px',
                    borderRadius: '6px',
                    backgroundColor: '#f7f7f7',
                    border: '1px solid #e0e0e0'
                }}>
                    <Avatar
                        src={commentAuthor?.profilePicture || undefined}
                        alt={commentAuthor?.username || 'Comment author'}
                        sx={{ width: 48, height: 48, bgcolor: '#e0e0e0' }}
                    >
                        {!commentAuthor?.profilePicture && (
                            <Person sx={{ fontSize: 28, color: '#777' }} />
                        )}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: '#142C5C', fontSize: '15px' }}>
                            {commentAuthor?.username ? `@${commentAuthor.username}` : 'Deleted user'}
                        </Typography>
                        {commentAuthor?.email && (
                            <Typography sx={{ fontSize: '13px', color: '#777' }}>
                                {commentAuthor.email}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{
                    padding: '15px',
                    borderRadius: '6px',
                    boxShadow: 'rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px'
                }}>
                    <TableContainer sx={{
                        maxHeight: 'calc(100vh - 200px)', boxShadow: 'none !important'
                    }} component={Paper}>
                        <Table sx={{ border: '1px solid rgba(224, 224, 224, 1)', minWidth: '100%', }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '14px',
                                            sm: '15px',
                                            md: '15px',
                                            lg: '16px',
                                            xl: '16px'
                                        }, color: '#000', fontWeight: '600', padding: {
                                            xs: '10px 5px',
                                            sm: '15px 10px',
                                            md: '15px 10px',
                                            lg: '20px 15px',
                                            xl: '20px 15px'
                                        }, verticalAlign: {
                                            xs: 'top',
                                            sm: 'top',
                                            md: 'top',
                                        },
                                        backgroundColor: '#e0e0e0'
                                    }}>Reporter
                                        Username</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '14px',
                                            sm: '15px',
                                            md: '15px',
                                            lg: '16px',
                                            xl: '16px'
                                        }, color: '#000', fontWeight: '600', padding: {
                                            xs: '10px 5px',
                                            sm: '15px 10px',
                                            md: '15px 10px',
                                            lg: '20px 15px',
                                            xl: '20px 15px'
                                        }, verticalAlign: {
                                            xs: 'top',
                                            sm: 'top',
                                            md: 'top',
                                        },
                                        backgroundColor: '#e0e0e0'
                                    }}>Reporter Email</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '14px',
                                            sm: '15px',
                                            md: '15px',
                                            lg: '16px',
                                            xl: '16px'
                                        }, color: '#000', fontWeight: '600', padding: {
                                            xs: '10px 5px',
                                            sm: '15px 10px',
                                            md: '15px 10px',
                                            lg: '20px 15px',
                                            xl: '20px 15px'
                                        }, verticalAlign: {
                                            xs: 'top',
                                            sm: 'top',
                                            md: 'top',
                                        },
                                        backgroundColor: '#e0e0e0'
                                    }}>Reason</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '14px',
                                            sm: '15px',
                                            md: '15px',
                                            lg: '16px',
                                            xl: '16px'
                                        }, color: '#000', fontWeight: '600', padding: {
                                            xs: '10px 5px',
                                            sm: '15px 10px',
                                            md: '15px 10px',
                                            lg: '20px 15px',
                                            xl: '20px 15px'
                                        }, verticalAlign: {
                                            xs: 'top',
                                            sm: 'top',
                                            md: 'top',
                                        },
                                        backgroundColor: '#e0e0e0'
                                    }}>Description</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '14px',
                                            sm: '15px',
                                            md: '15px',
                                            lg: '16px',
                                            xl: '16px'
                                        }, color: '#000', fontWeight: '600', padding: {
                                            xs: '10px 5px',
                                            sm: '15px 10px',
                                            md: '15px 10px',
                                            lg: '20px 15px',
                                            xl: '20px 15px'
                                        }, verticalAlign: {
                                            xs: 'top',
                                            sm: 'top',
                                            md: 'top',
                                        },
                                        backgroundColor: '#e0e0e0'
                                    }}>Date of Report</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportedUsers.map((user, index) => (<TableRow key={user?.id}>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px',
                                            sm: '13px',
                                            md: '14px',
                                            lg: '15px',
                                            xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px',
                                            sm: '10px',
                                            md: '10px',
                                            lg: '10px 15px',
                                            xl: '10px 15px'
                                        }
                                    }}>{(user?.username)}</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px',
                                            sm: '13px',
                                            md: '14px',
                                            lg: '15px',
                                            xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px',
                                            sm: '10px',
                                            md: '10px',
                                            lg: '10px 15px',
                                            xl: '10px 15px'
                                        }
                                    }}>{(user?.email)}</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px',
                                            sm: '13px',
                                            md: '14px',
                                            lg: '15px',
                                            xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px',
                                            sm: '10px',
                                            md: '10px',
                                            lg: '10px 15px',
                                            xl: '10px 15px'
                                        }
                                    }}>{user.reason}</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px',
                                            sm: '13px',
                                            md: '14px',
                                            lg: '15px',
                                            xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px',
                                            sm: '10px',
                                            md: '10px',
                                            lg: '10px 15px',
                                            xl: '10px 15px'
                                        }
                                    }}>{user.description}</TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px',
                                            sm: '13px',
                                            md: '14px',
                                            lg: '15px',
                                            xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px',
                                            sm: '10px',
                                            md: '10px',
                                            lg: '10px 15px',
                                            xl: '10px 15px'
                                        }
                                    }}>{formatDateString(user.date)}</TableCell>
                                </TableRow>))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>)}
            <AccountActionDialog
                open={accountDialogOpen}
                action="DISABLE_ACCOUNT"
                username={commentAuthor?.username}
                subject="comment"
                busy={accountActionBusy}
                onClose={() => setAccountDialogOpen(false)}
                onConfirm={handleDisableAccount}
            />
        </Box>
    </>);
};
export default CommentDetail;

